import { Box, Tabs, Tab, Button as MButton, ThemeProvider, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ColumnsPanelTrigger, Toolbar, ToolbarButton } from '@mui/x-data-grid-pro';

import { styled } from '@mui/material/styles';
import Button from 'components/generics/Button';
import MapParcels from 'components/generics/Map/MapParcels';
import More from 'components/icons/More';
import SaveIcon from '@mui/icons-material/Save';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getArableSurface,
  getCulturesByExploitationId,
  getGroundTypeByExploitationId,
  getParcelsByExploitation,
  updateParcel,
  exportCsvParcel as exportCsvParcelAPI,
} from 'services/API/Exploitation';
import { sendEvent } from 'utils/Event';
import { ArableContext, ExploitationContext } from 'utils/context';
import { displayErrorMessage, exportFileAsCSV, previousCultureRange } from 'utils/tools_functions';
import ParcelsForecast from './ParcelsForecast';
import DataGridParcels from './DataGridParcels';
import GroupParcelModal from './GroupParcelModal';
import AddEditParcelModal from './form/AddEditParcelModal';
import {
  customButtonTheme,
  customToolbarButtonTheme,
} from 'assets/styles/themes/generic_button_mui';
import { updateExploitationContext } from 'utils/Datagrid/CustomForecastDataGridUtils';
import dayjs from 'dayjs';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import '../../generics/mapLegend.style.scss';
import ProcedureCampaignModal from './modal/ProcedureCampaignModal';
import ProcedureOvertakeModal from './modal/ProcedureOvertakeModal';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKey,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import useAuth from 'providers/Auth/useAuth';
import { useTranslation } from 'react-i18next';
import './forecast.style.scss';
import { RolesNames } from 'components/generics/Interface/Api/Response/User/IUser';

const ParcelsToolbar = ({ showAllParcels, setShowAllParcels, hasHiddenParcels }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  return (
    <ThemeProvider theme={customToolbarButtonTheme}>
      <Toolbar>
        <Tooltip title={t('common.columns')}>
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" className="columnsPanelTrigger" />
            <div className="columnsPanelTriggerText">{t('common.columns').toUpperCase()}</div>
          </ColumnsPanelTrigger>
        </Tooltip>
        {user?.role === 'SUPER_ADMIN' && hasHiddenParcels && (
          <Tooltip
            title={showAllParcels ? t('datagrid.hideParcels') : t('datagrid.showAllParcels')}
          >
            <ToolbarButton onClick={() => setShowAllParcels((prev) => !prev)}>
              {showAllParcels ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </ToolbarButton>
          </Tooltip>
        )}
      </Toolbar>
    </ThemeProvider>
  );
};

const Parcels = ({ exploitation, setExploitation }) => {
  const [parcelUpdated, setParcelUpdated] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalGroup, setModalGroup] = useState(false);
  const [modalProcedure, setModalProcedure] = useState(false);
  const [modalOvertakeProcedure, setModalOvertakeProcedure] = useState(false);
  const [editData, setEditData] = useState(null);
  const [coordsExist, setCoordsExist] = useState(false);
  const [mapTab, setMap] = useState(false);
  const [dataGridLoading, setDataGridLoading] = useState(false);
  const [loadingComplete, setloadingComplete] = useState(false);
  const [value, setValue] = useState(0);
  const [isAllowedSubmitForecasts, setIsAllowedSubmitForecasts] = useState(false);
  const [isAllowedSubmitParcels, setIsAllowedSubmitParcels] = useState(false);
  const [showAllParcels, setShowAllParcels] = useState(false);
  const { setArableSurface } = useContext(ArableContext);
  const { exploitationContext, setExploitationContext, setParcelsInformations } =
    useContext(ExploitationContext);
  const forecastCampaign = exploitationContext?.forecastCampaign;
  const maxDate = dayjs().year(Number(forecastCampaign?.startYear)).month(6).date(31);
  const navigate = useNavigate();
  const [willOvertakeProcedure, setWillOvertakeProcedure] = useState(false);
  const stackedForecastRequestsRef = useRef([]);
  const stackedParcelsRequestsRef = useRef([]);
  const formSuccess = async () => {
    setModal(false);
    setModalGroup(false);
    setEditData(null);
    loadData();
  };

  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [hasInitialized, setHasInitialized] = useState(false);
  const exploitContextReloadNeeded =
    location?.state?.exploitContextReloadNeeded === false ? false : true;

  useEffect(() => {
    if (!exploitationContext) {
      if (localStorage.getItem('exploitation')) {
        setExploitation(false, +localStorage.getItem('exploitation'));
        setHasInitialized(true);
      } else {
        toast.info(t('message.selectExploitation'));
        navigate('/exploitation');
      }
    }
    // Dismount
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  useEffect(() => {
    if (exploitationContext && !hasInitialized) {
      if (exploitContextReloadNeeded === true) {
        loadData();
      }
      setHasInitialized(true);
    }
  }, [exploitationContext, hasInitialized]);

  useEffect(() => {
    if (exploitationContext && hasInitialized) {
      initData(exploitationContext.parcels, exploitationContext.arableTotalSurface);
    }
  }, [hasInitialized, exploitationContext]);

  const loadData = async () => {
    try {
      setDataGridLoading(true);

      const results = await Promise.all([
        getParcelsByExploitation(exploitationContext.id),
        getCulturesByExploitationId(exploitationContext.id, false),
        getGroundTypeByExploitationId(exploitationContext.id),
        getArableSurface(exploitationContext.id),
      ]);
      setExploitationContext((prevExploitationContext) => ({
        ...prevExploitationContext,
        parcels: results[0].data.data,
        cultures: results[1].data.data.sort((a, b) => a.name.localeCompare(b.name)),
        groundTypes: results[2].data.data,
      }));

      setParcelUpdated(!parcelUpdated);
      setDataGridLoading(false);
      initData(results[0].data.data, results[3].data.data);
    } catch (error) {
      if (error.message !== 'canceled') {
        toast.error(displayErrorMessage('ERR_GET_PARCEL'));
      }
    }
  };
  const initData = (parcels, arableTotalSurface) => {
    setCoordsExist(parcels?.some((p) => p.coordinates?.length > 0));
    setParcelsInformations(parcels);
    setArableSurface(arableTotalSurface);
    if (
      !exploitationContext.isForecastCampaignPossible &&
      exploitationContext.possibleCampaignChange.length > 0
    ) {
      setModalProcedure(true);
    }
    setloadingComplete(true);
  };

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const showMap = (bool) => {
    setMap(bool);
  };

  const exportCsvParcel = async () => {
    const response = await exportCsvParcelAPI(exploitationContext.id);
    exportFileAsCSV(response, exploitationContext.name);
  };

  const filteredParcels = useMemo(() => {
    if (showAllParcels) return exploitationContext?.parcels;
    if (exploitationContext?.parcels) {
      return filterParcelFromActivityDate(exploitationContext?.parcels, forecastCampaign);
    }
  }, [showAllParcels, exploitationContext?.parcels, forecastCampaign]);

  const hasHiddenParcels = useMemo(() => {
    if (!exploitationContext?.parcels) return false;
    const dateFiltered = filterParcelFromActivityDate(
      exploitationContext.parcels,
      forecastCampaign,
    );
    return dateFiltered.length !== exploitationContext.parcels.length;
  }, [exploitationContext?.parcels, forecastCampaign]);

  const updateMapParcel = (parcelId) => {
    const parcelToUpdate = exploitationContext?.parcels.find((parcel) => parcel.id === parcelId);

    const previousCultures = {};
    previousCultureRange().forEach((offset) => {
      const culture =
        parcelToUpdate?.parcelCultures?.[getCultureCampaignKey(offset, forecastCampaign)];
      previousCultures[`previousCulture${offset}`] = culture?.id ? culture.id : `EMPTY`;
    });

    const forecastCulture =
      parcelToUpdate?.parcelCultures?.[getCultureCampaignKey(0, forecastCampaign)];

    setEditData({
      name: parcelToUpdate.name,
      surface: parcelToUpdate.surface,
      organicState: parcelToUpdate.organicState,
      isIrrigable: parcelToUpdate.isIrrigable,
      groundType: parcelToUpdate.groundType.id || null,
      ...previousCultures,
      forecast: forecastCulture?.id ? forecastCulture.id : 0,
      id: parcelToUpdate.id,
      parentId: parcelToUpdate.parent,
      isParent: parcelToUpdate.isParent,
      conversionDate: parcelToUpdate.conversionDate,
    });
    setModal(true);
  };

  const previousCultureOrder = previousCultureRange({ reversed: true });

  const orderedFields = [
    'name',
    'surface',
    'conversionDate',
    'organicState',
    'isIrrigable',
    'groundType',
    ...previousCultureOrder.map((offset) => `previousCulture${offset}`),
    'activityStart',
    'activityEnd',
  ];

  const ExploitationParcelsMetadata = orderedFields.map((field, index) => ({
    field,
    position: index + 1,
    editable: true,
  }));

  const findIdByName = (data, key) => {
    if (key === t('message.noCultureAssociated')) {
      return null;
    }
    return exploitationContext[data].find((datum) => datum.name === key).id;
  };

  const updateParcelsExploitationContext = (data) => {
    const updatedParcels = data
      .map((parcel) => {
        if (parcel.children) {
          const clone = structuredClone(parcel);
          delete parcel.children;
          return [...clone?.children, parcel];
        }

        return parcel;
      })
      .flat();

    const allParcels = exploitationContext?.parcels.map((parcel) => {
      const updatedParcel = updatedParcels.find((updatedParcel) => updatedParcel.id === parcel.id);
      if (updatedParcel && parcel.id === updatedParcel.id) {
        return updatedParcel;
      }
      return parcel;
    });

    setExploitationContext((prevExploitationContext) => ({
      ...prevExploitationContext,
      parcels: allParcels,
    }));
  };

  //! TODO : uncomment for error conflict 409 and to reset the rows to their initial state
  const handleSubmit = async () => {
    if (stackedParcelsRequestsRef.current.length) {
      // const initialParcelsState = stackedParcelsRequestsRef.current.map((parcelToUpdate) => {
      //       const originalParcel = exploitationContext.parcels.find((p) => p.id === parcelToUpdate.id);
      //       return {
      //         id: parcelToUpdate.id,
      //         originalState: { ...originalParcel },
      //       };
      //     });
      stackedParcelsRequestsRef.current.map((parcelToUpdate) => {
        if (parcelToUpdate.hasOwnProperty('groundType')) {
          parcelToUpdate.groundType = findIdByName('groundTypes', parcelToUpdate['groundType']);
        }
        if (parcelToUpdate.hasOwnProperty('surface')) {
          parcelToUpdate.surface = Number(parcelToUpdate.surface);
        }

        const cultureKeysToUpdate = previousCultureRange().map((offset) => ({
          key: `previousCulture${offset}`,
          offset,
        }));

        let parcelCultures = [];

        cultureKeysToUpdate.forEach(({ key, offset }) => {
          if (parcelToUpdate.hasOwnProperty(key)) {
            if (parcelToUpdate[key]) {
              const startYear = forecastCampaign.startYear - offset;
              const endYear = forecastCampaign.endYear - offset;

              parcelCultures.push({
                campaign: {
                  stringFormat: `${startYear}-${endYear}`,
                  startYear,
                  endYear,
                },
                id: findIdByName('cultures', parcelToUpdate[key]),
              });
            }

            delete parcelToUpdate[key];
          }
        });

        parcelToUpdate.parcelCultures = parcelCultures;
      });
      try {
        const result = await updateParcel(
          exploitationContext.id,
          stackedParcelsRequestsRef.current,
        );
        const { data } = result.data;
        toast.success(t('success.parcelsUpdated'));
        updateParcelsExploitationContext(data);
      } catch (error) {
        // if (error?.response?.data?.status === 409) {
        //         const revertParcels = exploitationContext.parcels.map((parcel) => {
        //           const initialParcelState = initialParcelsState.find((p) => p.id === parcel.id);

        //           return initialParcelState ? initialParcelState.originalState : parcel;
        //         });

        //         setExploitationContext((prevExploitationContext) => ({
        //           ...prevExploitationContext,
        //           parcels: revertParcels,
        //         }));

        //         toast.error(
        //           `Conflit détecté : la culture ou le groupe est déjà utilisé par au moins une parcelle avec un statut bio différent.`
        //         );
        //       } else {
        //         toast.error(
        //           error?.response?.data?.message ??
        //             'Une erreur est survenue : impossible de mettre à jour les parcelles.'
        //         );
        //       }
        //     } finally {
        //       stackedParcelsRequestsRef.current = [];
        //       setIsAllowedSubmitParcels(false);
        //     }
        toast.error(error?.response?.data?.message ?? t('errors.ERR_UPDATE_PARCELS'));
      }
      stackedParcelsRequestsRef.current = [];
      setIsAllowedSubmitParcels(false);
    }

    if (stackedForecastRequestsRef.current.length) {
      const forecastParcelsCulturesToUpdate = stackedForecastRequestsRef.current.map((forecast) => {
        return {
          id: forecast.parcel,
          parcelCultures: {
            [`${forecastCampaign.startYear}-${forecastCampaign.endYear}`]: {
              id: forecast.culture,
              campaign: forecastCampaign,
              isFixed: forecast.isFixed,
            },
          },
        };
      });

      try {
        const result = await updateParcel(exploitationContext.id, forecastParcelsCulturesToUpdate);
        const { data } = result.data;
        updateExploitationContext(setExploitationContext, data);
        toast.success(t('success.forecastsUpdated'));
      } catch (error) {
        toast.error(error?.response?.data?.message ?? t('errors.ERR_UPDATE_FORECAST'));
      }
      stackedForecastRequestsRef.current = [];
      setIsAllowedSubmitForecasts(false);
    }
  };

  return (
    <div className="section">
      <h3 className="title_spacing">
        {t('message.nextCampaign', { campaign: forecastCampaign?.stringFormat })}
      </h3>
      <div className="main_container">
        <div className="contain-header">
          <div className="map_button_container">
            {!coordsExist && <h1>Parcelles</h1>}
            {!!coordsExist && (
              <div>
                <Button
                  defaultStyle="green_button"
                  text={t('common.parcels')}
                  backgroundColor={mapTab ? 'white' : '#277134'}
                  textColor={mapTab ? '#277134' : 'white'}
                  onClick={() => {
                    showMap(false);
                  }}
                />
                <Button
                  defaultStyle="green_button"
                  text={t('common.map')}
                  textColor={mapTab ? 'white' : '#277134'}
                  backgroundColor={mapTab ? '#277134' : 'white'}
                  onClick={() => {
                    showMap(true);
                  }}
                />
              </div>
            )}
            {user.role === RolesNames.SUPER_ADMIN && (
              <ThemeProvider theme={customButtonTheme}>
                <MButton sx={{ margin: '10px' }} onClick={exportCsvParcel}>
                  CSV
                </MButton>
              </ThemeProvider>
            )}
          </div>

          <div className="parcels_button_container">
            <ThemeProvider theme={customButtonTheme}>
              {!mapTab && (
                <MButton
                  onClick={handleSubmit}
                  disabled={!isAllowedSubmitParcels && !isAllowedSubmitForecasts}
                >
                  {t('button.saveChanges')} <SaveIcon sx={{ width: '18px' }} />
                </MButton>
              )}
              {exploitationContext?.possibleCampaignChange.length > 0 &&
                user?.functionalityRights.includes('CampaignChangeProcedure') && (
                  <MButton
                    disabled={!forecastCampaign}
                    onClick={() => {
                      setModalProcedure(true);
                    }}
                  >
                    {t('button.startProcedure')}
                  </MButton>
                )}

              <MButton
                onClick={() => {
                  (setModal(!modal), setEditData(null));
                }}
              >
                {t('common.add')}
                <More height={13} width={13} />
              </MButton>
              <MButton
                onClick={() => {
                  (setModalGroup(!modalGroup), setEditData(null));
                }}
              >
                {t('button.groupParcels')}
              </MButton>
            </ThemeProvider>
          </div>
        </div>
        {!!coordsExist && (
          <div style={{ display: mapTab ? 'block' : 'none' }}>
            <MapParcels
              updateParcel={updateMapParcel}
              forecastCampaign={forecastCampaign}
            ></MapParcels>
          </div>
        )}
        <div style={{ display: mapTab ? 'none' : 'block' }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', paddingLeft: '80px' }}>
              <StyledTabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <StyledTab label={t('common.parcels')} {...a11yProps(0)} />
                <StyledTab label={t('label.forecast')} {...a11yProps(1)} />
              </StyledTabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <DataGridParcels
                key={showAllParcels ? 'all-parcels' : 'filtered-parcels'}
                parcels={filteredParcels}
                forecastCampaign={exploitationContext?.forecastCampaign}
                cultures={exploitationContext?.cultures}
                groundTypes={exploitationContext?.groundTypes}
                id={exploitationContext?.id}
                refreshData={loadData}
                dataGridLoading={dataGridLoading}
                setEditData={setEditData}
                setModalGroup={setModalGroup}
                columnsMetadata={ExploitationParcelsMetadata}
                stackedParcelsRequestsRef={stackedParcelsRequestsRef}
                setIsAllowedSubmit={setIsAllowedSubmitParcels}
                maxDate={maxDate}
                customToolbar={
                  <ParcelsToolbar
                    showAllParcels={showAllParcels}
                    setShowAllParcels={setShowAllParcels}
                    hasHiddenParcels={hasHiddenParcels}
                  />
                }
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <ParcelsForecast
                exploitationContext={exploitationContext}
                dataGridLoading={dataGridLoading}
                setIsAllowedSubmit={setIsAllowedSubmitForecasts}
                stackedRequestsRef={stackedForecastRequestsRef}
              />
            </CustomTabPanel>
          </Box>
        </div>
      </div>
      {modal && (
        <AddEditParcelModal
          modal={modal}
          setModal={setModal}
          onSubmitSuccess={formSuccess}
          edit={editData}
          setExploitationContext={setExploitationContext}
          maxDate={maxDate}
          forecastCampaign={forecastCampaign}
          hideFormData={['isFixed']}
        />
      )}

      {modalGroup && (
        <GroupParcelModal
          modalGroup={modalGroup}
          setModalGroup={setModalGroup}
          onSubmitSuccess={() => formSuccess()}
          loadingComplete={loadingComplete}
          allParcels={exploitationContext?.parcels.filter(
            (parcel) => parcel.organicState !== ORGANIC_STATES.conversion,
          )}
          exploitation={exploitationContext}
          edit={editData}
        />
      )}
      {modalProcedure && (
        <div>
          <ProcedureCampaignModal
            modalProcedure={modalProcedure}
            setModalProcedure={setModalProcedure}
            possibleCampaign={exploitationContext?.possibleCampaignChange}
            setModalOvertakeProcedure={setModalOvertakeProcedure}
            willOvertakeProcedure={willOvertakeProcedure}
            setWillOvertakeProcedure={setWillOvertakeProcedure}
          />
        </div>
      )}
      {modalOvertakeProcedure && (
        <div>
          <ProcedureOvertakeModal
            modalOvertakeProcedure={modalOvertakeProcedure}
            setModalProcedure={setModalProcedure}
            setModalOvertakeProcedure={setModalOvertakeProcedure}
            setWillOvertakeProcedure={setWillOvertakeProcedure}
          />
        </div>
      )}
    </div>
  );
};

export default Parcels;

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StyledTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#e69636',
  },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: '#277134',
  '&:hover': {
    color: '#e69636',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: '#e69636',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));
